// Custom Session Manager for Multi-User Support
// This prevents the Firebase auth conflict where one user logout affects all users

class SessionManager {
  constructor() {
    this.sessions = new Map(); // Store active sessions
    this.sessionTimeout = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    this.currentTabId = this.generateTabId(); // Unique ID for this browser tab
    this.initializeFromStorage(); // Load sessions from localStorage on initialization
  }

  // Generate unique tab ID for this browser instance
  generateTabId() {
    return 'tab_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // Initialize sessions from localStorage on page load/refresh
  initializeFromStorage() {
    try {
      console.log('SessionManager: Initializing sessions from localStorage...');
      
      // Check for existing user session
      const userSessionData = localStorage.getItem('userSession');
      if (userSessionData) {
        console.log('Found user session data:', userSessionData);
        const sessionData = JSON.parse(userSessionData);
        if (this.isSessionValid(sessionData)) {
          this.sessions.set('userSession', sessionData);
          localStorage.setItem('userSessionId', 'userSession');
          console.log('User session restored successfully');
        } else {
          console.log('User session expired, removing...');
          localStorage.removeItem('userSession');
        }
      }

      // Check for existing admin session
      const adminSessionData = localStorage.getItem('adminSession');
      if (adminSessionData) {
        console.log('Found admin session data:', adminSessionData);
        const sessionData = JSON.parse(adminSessionData);
        if (this.isSessionValid(sessionData)) {
          this.sessions.set('adminSession', sessionData);
          localStorage.setItem('adminSessionId', 'adminSession');
          console.log('Admin session restored successfully');
        } else {
          console.log('Admin session expired, removing...');
          localStorage.removeItem('adminSession');
        }
      }

      // Check for existing tutor session
      const tutorSessionData = localStorage.getItem('tutorSession');
      if (tutorSessionData) {
        console.log('Found tutor session data:', tutorSessionData);
        const sessionData = JSON.parse(tutorSessionData);
        if (this.isSessionValid(sessionData)) {
          this.sessions.set('tutorSession', sessionData);
          localStorage.setItem('tutorSessionId', 'tutorSession');
          console.log('Tutor session restored successfully');
        } else {
          console.log('Tutor session expired, removing...');
          localStorage.removeItem('tutorSession');
        }
      }
      
      console.log('SessionManager: Initialization complete. Active sessions:', this.sessions.size);
    } catch (error) {
      console.error('Error initializing sessions from storage:', error);
      this.clearAllSessions();
    }
  }

  // Create a new user session
  createUserSession(userId, userData) {
    // Clear any existing sessions
    this.clearAllSessions();
    
    const session = {
      id: 'userSession',
      userId: userId,
      userData: userData,
      userType: '2', // Regular user
      createdAt: Date.now(),
      lastActivity: Date.now(),
      isActive: true
    };

    this.sessions.set('userSession', session);
    
    // Store session in localStorage for persistence across page refreshes
    localStorage.setItem('userSession', JSON.stringify(session));
    localStorage.setItem('userSessionId', 'userSession');
    localStorage.setItem('userType', '2');
    localStorage.setItem('currentUserId', userId);
    
    return 'userSession';
  }

  // Create admin session
  createAdminSession(adminData) {
    // Clear any existing sessions
    this.clearAllSessions();
    
    const session = {
      id: 'adminSession',
      userId: adminData.uid,
      userData: adminData,
      userType: '1', // Admin
      createdAt: Date.now(),
      lastActivity: Date.now(),
      isActive: true
    };

    this.sessions.set('adminSession', session);
    
    // Store session in localStorage for persistence across page refreshes
    localStorage.setItem('adminSession', JSON.stringify(session));
    localStorage.setItem('adminSessionId', 'adminSession');
    localStorage.setItem('userType', '1');
    localStorage.setItem('currentUserId', adminData.uid);
    
    return 'adminSession';
  }

  // Clear all sessions
  clearAllSessions() {
    // Clear from memory
    this.sessions.clear();
    
    // Clear from localStorage
    localStorage.removeItem('userSession');
    localStorage.removeItem('adminSession');
    localStorage.removeItem('tutorSession');
    localStorage.removeItem('userSessionId');
    localStorage.removeItem('adminSessionId');
    localStorage.removeItem('tutorSessionId');
    localStorage.removeItem('userType');
    localStorage.removeItem('currentUserId');
  }

  // Clear all sessions for the current tab (legacy method)
  clearTabSessions() {
    this.clearAllSessions();
  }

  // Get current session
  getCurrentSession() {
    // Check user session
    if (this.sessions.has('userSession')) {
      const session = this.sessions.get('userSession');
      if (this.isSessionValid(session)) {
        this.updateLastActivity(session.id);
        return session;
      } else {
        this.sessions.delete('userSession');
        localStorage.removeItem('userSession');
      }
    }
    
    // Check admin session
    if (this.sessions.has('adminSession')) {
      const session = this.sessions.get('adminSession');
      if (this.isSessionValid(session)) {
        this.updateLastActivity(session.id);
        return session;
      } else {
        this.sessions.delete('adminSession');
        localStorage.removeItem('adminSession');
      }
    }
    
    // Check tutor session
    if (this.sessions.has('tutorSession')) {
      const session = this.sessions.get('tutorSession');
      if (this.isSessionValid(session)) {
        this.updateLastActivity(session.id);
        return session;
      } else {
        this.sessions.delete('tutorSession');
        localStorage.removeItem('tutorSession');
      }
    }
    
    return null;
  }

  // Check if user is logged in
  isUserLoggedIn() {
    const session = this.getCurrentSession();
    return session && session.userType === '2';
  }

  // Check if admin is logged in
  isAdminLoggedIn() {
    const session = this.getCurrentSession();
    const isLoggedIn = session && session.userType === '1';
    console.log('isAdminLoggedIn check:', { session, userType: session?.userType, isLoggedIn });
    return isLoggedIn;
  }

  // Check if tutor is logged in
  isTutorLoggedIn() {
    const session = this.getCurrentSession();
    return session && session.userType === '3';
  }

  // Get current user data
  getCurrentUser() {
    const session = this.getCurrentSession();
    return session ? session.userData : null;
  }

  // Get current user type
  getCurrentUserType() {
    const session = this.getCurrentSession();
    return session ? session.userType : null;
  }

  // Get current user ID
  getCurrentUserId() {
    const session = this.getCurrentSession();
    return session ? session.userId : null;
  }

  // Update last activity
  updateLastActivity(sessionId) {
    const session = this.sessions.get(sessionId);
    if (session) {
      session.lastActivity = Date.now();
      this.sessions.set(sessionId, session);
      // Update localStorage as well
      localStorage.setItem(sessionId, JSON.stringify(session));
    }
  }

  // Logout current user
  logoutCurrentUser() {
    this.clearAllSessions();
  }

  // Clean up expired sessions
  cleanupExpiredSessions() {
    const now = Date.now();
    for (const [sessionId, session] of this.sessions.entries()) {
      if (now - session.lastActivity > this.sessionTimeout) {
        this.sessions.delete(sessionId);
      }
    }
  }

  // Generate unique session ID
  generateSessionId() {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // Check if session is valid
  isSessionValid(session) {
    if (!session) return false;
    
    // For tutor sessions stored directly in localStorage (not through sessionManager)
    // Check if it has required fields and status is approved
    if (session.status === 'approved' && session.uid) {
      // If lastActivity exists, check timeout
      if (session.lastActivity) {
        const now = Date.now();
        return (now - session.lastActivity) <= this.sessionTimeout;
      }
      // If no lastActivity, assume valid (for backward compatibility)
      return true;
    }
    
    // For sessions managed by sessionManager
    if (!session.isActive) return false;
    
    const now = Date.now();
    // If lastActivity doesn't exist, set it to now
    if (!session.lastActivity) {
      session.lastActivity = now;
    }
    return (now - session.lastActivity) <= this.sessionTimeout;
  }

  // Get all active sessions (for debugging)
  getAllSessions() {
    return Array.from(this.sessions.values());
  }

  // Force logout specific user (admin only)
  forceLogoutUser(userId) {
    for (const [sessionId, session] of this.sessions.entries()) {
      if (session.userId === userId) {
        this.sessions.delete(sessionId);
      }
    }
  }

  // Validate all sessions
  validateAllSessions() {
    let hasValidSession = false;
    
    // Check user session
    if (this.sessions.has('userSession')) {
      const session = this.sessions.get('userSession');
      if (this.isSessionValid(session)) {
        hasValidSession = true;
      } else {
        this.sessions.delete('userSession');
        localStorage.removeItem('userSession');
      }
    }
    
    // Check admin session
    if (this.sessions.has('adminSession')) {
      const session = this.sessions.get('adminSession');
      if (this.isSessionValid(session)) {
        hasValidSession = true;
      } else {
        this.sessions.delete('adminSession');
        localStorage.removeItem('adminSession');
      }
    }
    
    // Check tutor session
    if (this.sessions.has('tutorSession')) {
      const session = this.sessions.get('tutorSession');
      if (this.isSessionValid(session)) {
        hasValidSession = true;
      } else {
        this.sessions.delete('tutorSession');
        localStorage.removeItem('tutorSession');
      }
    }
    
    return hasValidSession;
  }
}

// Create singleton instance
const sessionManager = new SessionManager();

// Clean up expired sessions every hour
setInterval(() => {
  sessionManager.cleanupExpiredSessions();
}, 60 * 60 * 1000);

// Validate sessions when page loads
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    sessionManager.validateAllSessions();
  });
}

export default sessionManager;

